import React, {ChangeEvent, useEffect, useState} from 'react';
import {createControl, updateControl} from "../../domain/validation/validation";
import CountryAndStateFields from "../../ui/forms/fields/country-and-state-fields";
import {rootActions, useAppDispatch, useAppSelector} from "./data/store";
import {getAllDealers, getCountriesAndStates, postGetDealers} from "./data-service";
import {ICountry, ICountryState} from "../../domain/addresses/interfaces";
import SelectSimpleField from "../../ui/forms/fields/select-simple-field";
import {cloneDeep} from "lodash";
import DealersResult from "./dealer-result";
import {IDealerResultResponse} from "./data/interfaces";
import {useWindowSize} from "../../domain/common";
import { USA_ID } from '../../domain/addresses/addresses-provider';


/**
 * https://wavesaudio.atlassian.net/browse/DSWEB-2787
 * Created by Raz Liveyatan 01.03.2023
 * */

const App = () => {
    const [dealers, setDealers] = useState([]);
    const [selectedCountryId, setSelectedCountryId] = useState('');
    const [filteredDealers, setFilteredDealers] = useState([]);
    const countries = useAppSelector(store => store.countries);
    const countryIdControl = useAppSelector(store => store.countryIdControl);
    const stateIdControl = useAppSelector(store => store.stateIdControl);
    const categoryIdControl = useAppSelector(store => store.categoryIdControl);
    const productList = useAppSelector(store => store.ProductList);
    const isFirstLoad = useAppSelector(store => store.isFirstLoad);
    const size = useWindowSize();
    const isTabletOrLess = (size.width !== undefined && size.width <= 1029);
    const dispatch = useAppDispatch();


    /**
     * set deep link, storage, and initial data on page load
     */
    useEffect(() => {
        (async () => {
            if (window.location.pathname === '/all-dealers') {
                const result = await getAllDealers(1);
                if (result && result.data && result.data.relatedData){
                    const _dealers = result.data.relatedData;
                    setDealers(_dealers);
                    setFilteredDealers(_dealers);
                    dispatch(
                        rootActions.main({
                            isFirstLoad:false,
                        }));
                }
            }
            else {
                const _countries = await getCountriesAndStates();
                if (_countries) {
                    const parsedCountries = JSON.parse((_countries.data.relatedData));

                    const countriesMap = new Map();
                    parsedCountries.countries.forEach((country: ICountry) => {
                        country.states = country.states || [];
                        countriesMap.set(country.countryID, country)
                    });

                    // add states to countries
                    if (parsedCountries.states) {
                        for (let i = 0; i < parsedCountries.states.length; i++) {
                            const state: ICountryState = parsedCountries.states[i];
                            if (!countriesMap.has(state.countryID)) continue;

                            const country: ICountry = countriesMap.get(state.countryID);
                            country.states.push(state);
                        }
                    }

                    // Make the USA first in the list
                    // https://wavesaudio.atlassian.net/browse/MRKT-25844
                    const usa = countriesMap.get(USA_ID);
                    countriesMap.delete(USA_ID);

                    const countries: ICountry[] = Array.from(countriesMap.values());
                    countries.unshift(usa);

                    dispatch(
                        rootActions.main({
                            countries: countries
                        })
                    );
                }
            }
        })();
    }, [dispatch]);


    /**
     * get Dealers by countryID or CountryID and StateID
     * */
    const getDealers = async (countryId: string, stateId: string) => {
        if (countryId !== '0' && countryId !== '') {
            const dealers = await postGetDealers(countryId, stateId);
            if (dealers && dealers.data && dealers.data.relatedData) {
                const result = dealers.data.relatedData.filter((dealer: any) => !dealer.inactive);
                setDealers(result);
                setFilteredDealersByCategory(categoryIdControl.value !== '' ? categoryIdControl.value : 'Software', result);
            }
        } else {
            setDealers([]);
            setFilteredDealers([]);
        }
    }

    /**
     * On input change
     */
    const handleChangeEvent = async (evt: ChangeEvent) => {
        const $target = evt.target as HTMLInputElement;
        const category = $target.value;
        const _dealers = cloneDeep(dealers);
        setFilteredDealersByCategory(category, _dealers);
    };

    const setFilteredDealersByCategory = (category:string, dealers:never[]) => {
        const _dealers = dealers;
        console.log('_dealers', _dealers);
        if (_dealers.length > 0) {
            let filtered: any = [];
            switch (category) {
                case 'Software':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.software);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                case 'DiGiGrid':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.diGiGrid);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                case 'eMotion':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.eMotion);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                case 'Live':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.live);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                case 'DiGiCo':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.diGiCo);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                case 'VENUE':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.venue);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                case 'CommAudio':
                    filtered = _dealers.filter((dealer: IDealerResultResponse) => dealer.commAudio);
                    if (filtered) {
                        setFilteredDealers(filtered);
                    }
                    break;
                default:
                    setFilteredDealers(_dealers);
                    break;
            }
        }
        dispatch(
            rootActions.setControl({
                controlName: 'categoryIdControl',
                control: updateControl(categoryIdControl, category, !categoryIdControl.isValid),
            })
        );
        dispatch(
            rootActions.main({
                isFirstLoad:false,
            })
        );
    }

    return (
        <>
            {
                countries && countries.length > 0 &&
                <div className="w-full">
                    <div className={`md:flex ${isTabletOrLess ? 'flex-wrap' : ''}`}>
                        <CountryAndStateFields
                            idPrefix="countries"
                            countries={countries}
                            countryIdControl={countryIdControl}
                            stateIdControl={stateIdControl}
                            rootClasses={`flex ${isTabletOrLess ? 'flex-wrap' : ''}`}
                            selectClasses={`rounded mr-2 w-80`}
                            onBlur={() => {
                            }}
                            onCountryChange={async evt => {
                                // on country change:
                                // check if the chosen country has states ( for state input )
                                // if the chosen country has states, set it's control to required.
                                setDealers([]);
                                setFilteredDealers([]);
                                let hasStates = false;
                                let selectedCountry2lettersCode = '';
                                const $target = evt.target as HTMLInputElement;
                                if ($target.value) {
                                    let country = countries.find(country => country.countryID.toString() === $target.value.toString());
                                    if (country) {
                                        selectedCountry2lettersCode = country.country2Digit;
                                        if (country.states && country.states.length > 0) {
                                            hasStates = true;
                                        }
                                    }
                                }
                                dispatch(
                                    rootActions.main({
                                        stateIdControl: createControl(
                                            'stateId',
                                            0, {
                                                required: hasStates
                                            }),
                                        countryIdControl: updateControl(countryIdControl, Number($target.value), true)
                                    })
                                );
                                if (!hasStates) {
                                    await getDealers(selectedCountry2lettersCode, '');
                                }
                                setSelectedCountryId(selectedCountry2lettersCode);
                            }}
                            onStateChange={async evt => {
                                const $target = evt.target as HTMLInputElement;
                                dispatch(
                                    rootActions.main({
                                        stateIdControl: updateControl(stateIdControl, Number($target.value), true)
                                    })
                                );
                                if ($target.value) {
                                    let country = countries.find(country => country.country2Digit === selectedCountryId);
                                    if (country) {
                                        let state = country.states.find(state => state.stateID.toString() === $target.value);
                                        if (state) {
                                            await getDealers(selectedCountryId, state.state2Digit);
                                        }
                                    }
                                }
                            }}
                            onClick={() => {
                            }}
                        />
                        {
                            productList &&
                            <div className={'md:flex'}>
                                <SelectSimpleField
                                    id="product-select"
                                    label="Category"
                                    control={categoryIdControl}
                                    rootClasses={`mb-6 mt-3 md:mt-0 w-80`}
                                    selectClasses={'rounded'}
                                    initialSelectionText={''}
                                    onChange={handleChangeEvent}
                                    onBlur={handleChangeEvent}
                                    options={productList ? productList.map(product => {
                                        return {
                                            key: product.key,
                                            value: product.value,
                                            text: product.text,
                                        }
                                    }) : []}
                                />
                            </div>
                        }
                    </div>
                </div>
            }
            {
               filteredDealers && filteredDealers.length > 0 && <DealersResult results={filteredDealers}/>
            }
            {
                countryIdControl.value !== '' && !isFirstLoad && filteredDealers.length === 0 && <div className={'mx-4'}>
                    No Dealers Found
                </div>
            }
        </>
    )
}

export default App;